"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useApolloClient } from "@apollo/client/react";
import { Check } from "lucide-react";

import {
	Button,
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
	Popover,
	PopoverContent,
	PopoverTrigger,
	cn,
} from "@jasperlabs/ui";

import { selectScenarioAction } from "./actions";

type MockScenarioOption = {
	name: string;
	label: string;
	description: string;
};

type Props = {
	scenarios: MockScenarioOption[];
	activeName: string;
	/** Runs after the cookie is set, before the current route refreshes. */
	onApplied?: () => Promise<void>;
};

/**
 * Floating scenario switch for mock mode.
 *
 * Sits on the bottom-right of every page so a reviewer can change what the
 * app shows without leaving the screen they are looking at.
 */
export function MockScenarioToolbar({
	scenarios,
	activeName,
	onApplied,
}: Props) {
	const router = useRouter();
	const [open, setOpen] = useState(false);
	const [applying, setApplying] = useState(false);
	const [pendingName, setPendingName] = useState<string | null>(null);

	// The optimistic name is only useful until the refreshed server prop matches
	// it. Adjusting here, rather than in an effect, avoids a second paint.
	// That refresh can arrive while resetStore is still running, so `applying`
	// stays set until the callback finishes and keeps a second selection out.
	if (pendingName !== null && pendingName === activeName) {
		setPendingName(null);
	}

	const selectedName = pendingName ?? activeName;
	const shownLabel =
		scenarios.find((scenario) => scenario.name === selectedName)?.label ??
		selectedName;
	const busy = pendingName !== null || applying;

	async function select(name: string) {
		if (busy) {
			return;
		}

		if (name === activeName) {
			setOpen(false);
			return;
		}

		setPendingName(name);
		setApplying(true);
		setOpen(false);

		try {
			const formData = new FormData();
			formData.set("scenario", name);
			await selectScenarioAction(formData);
			try {
				await onApplied?.();
			} catch {
				// resetStore rejects when a query is in flight. The cookie is
				// already set, so the refresh still picks up the new scenario.
			}
			router.refresh();
		} catch {
			setPendingName(null);
		} finally {
			setApplying(false);
		}
	}

	return (
		<div className="fixed right-4 bottom-4 z-[2000]">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						className="shadow-lg"
						aria-label={`Mock scenario: ${shownLabel}`}
						loading={busy}
						data-testid="mock-scenario-toolbar"
					>
						{shownLabel}
					</Button>
				</PopoverTrigger>
				<PopoverContent side="top" align="end" className="w-80 p-0">
					<Command>
						<CommandInput placeholder="Search scenarios" />
						<CommandList>
							<CommandEmpty className="py-6">No scenarios</CommandEmpty>
							{scenarios.map((scenario) => {
								const isActive = scenario.name === selectedName;

								return (
									<CommandItem
										key={scenario.name}
										value={scenario.label}
										keywords={[scenario.name, scenario.description]}
										disabled={busy}
										data-testid={`select-scenario-${scenario.name}`}
										className={cn("items-start", isActive && "bg-neutral-100")}
										onSelect={() => {
											void select(scenario.name);
										}}
									>
										<span className="flex min-w-0 flex-1 flex-col gap-0.5">
											<span>{scenario.label}</span>
											<span className="text-xs text-neutral-500">
												{scenario.description}
											</span>
										</span>
										{isActive ? (
											<Check className="size-3.5 shrink-0" aria-hidden />
										) : null}
									</CommandItem>
								);
							})}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
}

/** Same toolbar, wired to drop the Apollo cache so active queries refetch. */
export function MockScenarioToolbarWithApollo(props: Omit<Props, "onApplied">) {
	const client = useApolloClient();

	return (
		<MockScenarioToolbar
			{...props}
			onApplied={async () => {
				await client.resetStore();
			}}
		/>
	);
}
